// src/contexts/AuthContext.tsx

import { createContext, useEffect, useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

// Firebase imports (preserved for rollback capability)
import { auth } from '../config/firebase'
import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signOut,
	onAuthStateChanged,
	sendPasswordResetEmail,
} from 'firebase/auth'

// API authentication imports
import ApiAuthService from '../services/api-auth.service'
import authConfig from '../config/auth'
import {
	User,
	UnifiedUser,
	UserType,
	EmailLoginCredentials,
	IdentifierLoginCredentials,
	EmailPasswordReset,
	OtpPasswordReset,
	EmailPasswordResetConfirm,
	OtpPasswordResetConfirm,
} from '../types/auth.types'

type AuthContextType = {
	user: UnifiedUser | User | null
	isAuthenticated: boolean
	isLoading: boolean
	// Multi-role authentication methods
	loginMultiRole: (
		userType: UserType,
		credentials: EmailLoginCredentials | IdentifierLoginCredentials,
	) => Promise<void>
	forgotPasswordEmail: (data: EmailPasswordReset) => Promise<void>
	forgotPasswordOTP: (data: OtpPasswordReset) => Promise<void>
	resetPasswordEmail: (data: EmailPasswordResetConfirm) => Promise<void>
	resetPasswordOTP: (data: OtpPasswordResetConfirm) => Promise<void>
	// Legacy methods for backward compatibility-
	login: (email: string, password: string) => Promise<void>
	register: (email: string, password: string, name: string) => Promise<void>
	logout: () => Promise<void>
	resetPassword: (email: string) => Promise<void>
	checkAuthStatus: () => boolean
	getAuthHeaders: () => { Authorization: string } | {}
	hasRole: (role: string | string[]) => boolean
	// New helper methods
	getUserType: () => UserType | null
	isUserSuspended: () => boolean
	canRefreshToken: () => boolean
}

const AuthContext = createContext<AuthContextType>({
	user: null,
	isAuthenticated: false,
	isLoading: true,
	// Multi-role methods
	loginMultiRole: async () => {},
	forgotPasswordEmail: async () => {},
	forgotPasswordOTP: async () => {},
	resetPasswordEmail: async () => {},
	resetPasswordOTP: async () => {},
	// Legacy methods
	login: async () => {},
	register: async () => {},
	logout: async () => {},
	resetPassword: async () => {},
	checkAuthStatus: () => false,
	getAuthHeaders: () => ({}),
	hasRole: () => false,
	// New helper methods
	getUserType: () => null,
	isUserSuspended: () => false,
	canRefreshToken: () => false,
})

type AuthProviderProps = {
	children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
	const [user, setUser] = useState<UnifiedUser | User | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const navigate = useNavigate()

	// Initialize authentication based on provider
	useEffect(() => {
		const initializeAuth = async () => {
			setIsLoading(true)

			if (authConfig.provider === 'api') {
				// API Authentication initialization
				try {
					const currentUser = ApiAuthService.getCurrentUser()
					if (currentUser && ApiAuthService.isAuthenticated()) {
						setUser(currentUser)
					} else {
						setUser(null)
					}
				} catch (error) {
					console.error('API auth initialization error:', error)
					setUser(null)
				}
				setIsLoading(false)
			} else {
				// Firebase Authentication initialization (preserved for rollback)
				if (!auth) {
					console.warn(
						'Firebase auth not initialized. Check Firebase configuration.',
					)
					setUser(null)
					setIsLoading(false)
					return
				}

				const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
					if (firebaseUser) {
						setUser({
							uid: firebaseUser.uid,
							email: firebaseUser.email || '',
							username: firebaseUser.displayName || undefined,
							isActive: true,
							profileImageUrl: firebaseUser.photoURL || null,
							createdAt: new Date(firebaseUser.metadata.creationTime || ''),
							updatedAt: new Date(firebaseUser.metadata.lastSignInTime || ''),
							lastLoginAt: new Date(firebaseUser.metadata.lastSignInTime || ''),
						})
					} else {
						setUser(null)
					}
					setIsLoading(false)
				})

				return () => unsubscribe()
			}
		}

		initializeAuth()
	}, [])

	// Multi-role login method
	const loginMultiRole = async (
		userType: UserType,
		credentials: EmailLoginCredentials | IdentifierLoginCredentials,
	) => {
		try {
			setIsLoading(true)

			if (authConfig.provider === 'api') {
				// Check if user is suspended before login
				if (ApiAuthService.isUserSuspended()) {
					throw new Error('Account suspended. Please contact support.')
				}

				const unifiedUser = await ApiAuthService.login(userType, credentials)
				setUser(unifiedUser)

				// Navigate based on user type
				const routeMap = {
					'default-administrator': '/admin',
					administrator: '/admin',
					driver: '/driver/dashboard',
					passenger: '/passenger/dashboard',
				}

				navigate(routeMap[userType] || '/admin')
			} else {
				// Firebase Authentication (preserved for rollback)
				if (!auth) {
					throw new Error(
						'Firebase auth not initialized. Check Firebase configuration.',
					)
				}

				if (
					userType === 'default-administrator' ||
					userType === 'administrator'
				) {
					const emailCreds = credentials as EmailLoginCredentials
					await signInWithEmailAndPassword(
						auth,
						emailCreds.email,
						emailCreds.password,
					)
				} else {
					throw new Error('Firebase does not support identifier-based login')
				}
			}
		} catch (error) {
			console.error('Multi-role login error:', error)
			throw error
		} finally {
			setIsLoading(false)
		}
	}

	// Legacy login method for backward compatibility
	const login = async (email: string, password: string) => {
		return loginMultiRole('default-administrator', { email, password })
	}

	const register = async (email: string, password: string, name: string) => {
		try {
			setIsLoading(true)

			if (authConfig.provider === 'api') {
				// API registration not implemented yet
				throw new Error('Registration via API is not implemented yet')
			} else {
				// Firebase registration (preserved for rollback)
				if (!auth) {
					throw new Error(
						'Firebase auth not initialized. Check Firebase configuration.',
					)
				}

				const { user: firebaseUser } = await createUserWithEmailAndPassword(
					auth,
					email,
					password,
				)

				// Store additional user info in Firestore
				await createUserProfile(firebaseUser.uid, {
					email,
					name,
				})
			}
		} catch (error) {
			console.error('Registration error:', error)
			throw error
		} finally {
			setIsLoading(false)
		}
	}

	const logout = async () => {
		try {
			if (authConfig.provider === 'api') {
				// API logout
				await ApiAuthService.logout()
				setUser(null)
			} else {
				// Firebase logout (preserved for rollback)
				if (!auth) {
					throw new Error(
						'Firebase auth not initialized. Check Firebase configuration.',
					)
				}
				await signOut(auth)
			}
			navigate('/login')
		} catch (error) {
			console.error('Logout error:', error)
			throw error
		}
	}

	// Multi-role password reset methods
	const forgotPasswordEmail = async (
		data: EmailPasswordReset,
		userType: UserType = 'administrator',
	) => {
		try {
			if (authConfig.provider === 'api') {
				await ApiAuthService.forgotPassword(userType, data)
			} else {
				if (!auth) {
					throw new Error(
						'Firebase auth not initialized. Check Firebase configuration.',
					)
				}
				await sendPasswordResetEmail(auth, data.email)
			}
		} catch (error) {
			console.error('Email password reset error:', error)
			throw error
		}
	}

	const forgotPasswordOTP = async (
		data: OtpPasswordReset,
		userType: UserType = 'driver',
	) => {
		try {
			if (authConfig.provider === 'api') {
				await ApiAuthService.forgotPassword(userType, data)
			} else {
				throw new Error(
					'OTP-based password reset is not supported with Firebase',
				)
			}
		} catch (error) {
			console.error('OTP password reset error:', error)
			throw error
		}
	}

	const resetPasswordEmail = async (
		data: EmailPasswordResetConfirm,
		userType: UserType = 'administrator',
	) => {
		try {
			if (authConfig.provider === 'api') {
				await ApiAuthService.resetPassword(userType, data)
			} else {
				throw new Error(
					'Email-based password reset confirmation is not supported with Firebase',
				)
			}
		} catch (error) {
			console.error('Email password reset confirmation error:', error)
			throw error
		}
	}

	const resetPasswordOTP = async (
		data: OtpPasswordResetConfirm,
		userType: UserType = 'driver',
	) => {
		try {
			if (authConfig.provider === 'api') {
				await ApiAuthService.resetPassword(userType, data)
			} else {
				throw new Error(
					'OTP-based password reset confirmation is not supported with Firebase',
				)
			}
		} catch (error) {
			console.error('OTP password reset confirmation error:', error)
			throw error
		}
	}

	// Legacy password reset method for backward compatibility
	const resetPassword = async (email: string) => {
		return forgotPasswordEmail({ email })
	}

	const checkAuthStatus = (): boolean => {
		if (authConfig.provider === 'api') {
			return ApiAuthService.isAuthenticated()
		} else {
			return !!user
		}
	}

	const getAuthHeaders = () => {
		if (authConfig.provider === 'api') {
			return ApiAuthService.getAuthHeaders()
		} else {
			// For Firebase, you might need to get the ID token
			return {}
		}
	}

	// Helper function to check user role
	const hasRole = (role: string | string[]): boolean => {
		if (!user) return false

		if (authConfig.provider === 'api') {
			const unifiedUser = user as UnifiedUser

			// Check if user has role array (administrator type)
			if (unifiedUser.role && Array.isArray(unifiedUser.role)) {
				if (Array.isArray(role)) {
					return role.some(r => unifiedUser.role!.includes(r))
				}
				return unifiedUser.role.includes(role)
			}

			// For user types without role array, check userType
			if (Array.isArray(role)) {
				return role.includes(unifiedUser.userType)
			}
			return unifiedUser.userType === role
		}

		// Firebase role checking (preserved for rollback)
		return true // Placeholder for Firebase role checking
	}

	// New helper methods
	const getUserType = (): UserType | null => {
		if (authConfig.provider === 'api') {
			const unifiedUser = user as UnifiedUser
			return unifiedUser?.userType || null
		}
		return null
	}

	const isUserSuspended = (): boolean => {
		if (authConfig.provider === 'api') {
			const unifiedUser = user as UnifiedUser
			return unifiedUser?.isSuspended || false
		}
		return false
	}

	const canRefreshToken = (): boolean => {
		if (authConfig.provider === 'api') {
			const userType = getUserType()
			return userType !== 'default-administrator' && !!user
		}
		return false
	}

	// Helper function for Firebase user profile creation (preserved for rollback)
	const createUserProfile = async (
		uid: string,
		data: { email: string; name: string },
	) => {
		// This would be implemented in your firebase.service.ts
		// For now, just a placeholder
		console.log('Creating user profile:', uid, data)
	}

	return (
		<AuthContext.Provider
			value={{
				user,
				isAuthenticated: !!user,
				isLoading,
				// Multi-role methods
				loginMultiRole,
				forgotPasswordEmail,
				forgotPasswordOTP,
				resetPasswordEmail,
				resetPasswordOTP,
				// Legacy methods
				login,
				register,
				logout,
				resetPassword,
				checkAuthStatus,
				getAuthHeaders,
				hasRole,
				// New helper methods
				getUserType,
				isUserSuspended,
				canRefreshToken,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export default AuthContext
